import { useCallback, useEffect, useState } from "react";
import {
  FormAlert,
  FormField,
  FormGrid,
  SettingsButton,
} from "@NP-Webber/crm-kit";
import {
  financeApi,
  formatShekels,
  type Balance,
  type Expense,
  type Payment,
} from "../../api/financeApi";
import { toClientApiError } from "../../api/httpClient";

export type CustomerFinancePanelProps = {
  customerId: string;
  archived: boolean;
};

function shekelsToAgorot(value: string): number | null {
  const normalized = value.replace(",", ".").trim();
  if (!normalized) {
    return null;
  }
  const num = Number(normalized);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return Math.round(num * 100);
}

export function CustomerFinancePanel({
  customerId,
  archived,
}: CustomerFinancePanelProps) {
  const [balance, setBalance] = useState<Balance | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [payAmount, setPayAmount] = useState("");
  const [payReference, setPayReference] = useState("");
  const [expenseTitle, setExpenseTitle] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const [bal, pays, exps] = await Promise.all([
        financeApi.getBalance(customerId),
        financeApi.listPayments(customerId),
        financeApi.listExpenses(customerId),
      ]);
      setBalance(bal);
      setPayments(pays);
      setExpenses(exps);
    } catch (err) {
      setError(toClientApiError(err).message);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const balanceLabel =
    balance == null
      ? "—"
      : `${formatShekels(balance.amountAgorot)} ₪${
          balance.amountAgorot > 0
            ? " (חוב)"
            : balance.amountAgorot < 0
              ? " (זכות)"
              : " (סגור)"
        }`;

  return (
    <div className="related-panel related-panel--finance">
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}

      <div className="related-panel__item">
        <strong>יתרה תפעולית</strong>
        <span>{balanceLabel}</span>
        <p className="related-panel__hint">
          היתרה נגזרת מתנועות בלבד — אין עריכה ידנית. מנויים מנוהלים בלשונית «מנויים».
        </p>
      </div>

      <h3 className="related-panel__subtitle">תשלומים</h3>
      {payments.length === 0 ? (
        <p className="related-panel__empty">אין תשלומים.</p>
      ) : (
        <ul className="related-panel__list">
          {payments.map((pay) => (
            <li key={pay.id} className="related-panel__item">
              <strong>{formatShekels(pay.amountAgorot)} ₪</strong>
              <span>
                {pay.method}
                {pay.reference ? ` · ${pay.reference}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!archived ? (
        <FormGrid cols={2}>
          <FormField
            label="סכום (₪)"
            value={payAmount}
            onChange={setPayAmount}
            disabled={busy}
          />
          <FormField
            label="אסמכתא"
            value={payReference}
            onChange={setPayReference}
            disabled={busy}
          />
          <SettingsButton
            type="button"
            variant="primarySettings"
            size="sm"
            disabled={busy || !payAmount.trim()}
            onClick={() => {
              const amountAgorot = shekelsToAgorot(payAmount);
              if (amountAgorot == null) {
                setError("סכום תשלום לא תקין");
                return;
              }
              void run(async () => {
                await financeApi.createPayment({
                  customerId,
                  amountAgorot,
                  method: "manual",
                  reference: payReference.trim() || undefined,
                });
                setPayAmount("");
                setPayReference("");
              });
            }}
          >
            רישום תשלום
          </SettingsButton>
        </FormGrid>
      ) : null}

      <h3 className="related-panel__subtitle">הוצאות</h3>
      {expenses.length === 0 ? (
        <p className="related-panel__empty">אין הוצאות משויכות.</p>
      ) : (
        <ul className="related-panel__list">
          {expenses.map((exp) => (
            <li key={exp.id} className="related-panel__item">
              <strong>{exp.title}</strong>
              <span>{formatShekels(exp.amountAgorot)} ₪</span>
            </li>
          ))}
        </ul>
      )}
      {!archived ? (
        <FormGrid cols={2}>
          <FormField
            label="כותרת הוצאה"
            value={expenseTitle}
            onChange={setExpenseTitle}
            disabled={busy}
          />
          <FormField
            label="סכום (₪)"
            value={expenseAmount}
            onChange={setExpenseAmount}
            disabled={busy}
          />
          <SettingsButton
            type="button"
            variant="primarySettings"
            size="sm"
            disabled={busy || !expenseTitle.trim() || !expenseAmount.trim()}
            onClick={() => {
              const amountAgorot = shekelsToAgorot(expenseAmount);
              if (amountAgorot == null) {
                setError("סכום הוצאה לא תקין");
                return;
              }
              void run(async () => {
                await financeApi.createExpense({
                  customerId,
                  title: expenseTitle.trim(),
                  amountAgorot,
                });
                setExpenseTitle("");
                setExpenseAmount("");
              });
            }}
          >
            רישום הוצאה
          </SettingsButton>
        </FormGrid>
      ) : null}
    </div>
  );
}
